import { EmpireAPIError } from "@empireapi/client";

export type ErrorKind =
  | "validation"
  | "authentication"
  | "forbidden"
  | "not-found"
  | "conflict"
  | "rate-limit"
  | "transient"
  | "unexpected";

export interface PresentedError {
  kind: ErrorKind;
  title: string;
  message: string;
  status: number | null;
  code: string | null;
  requestId: string | null;
  validationIssues: readonly string[];
}

function validationIssues(errors: Record<string, unknown>[] | null): string[] {
  if (!errors) return [];

  return errors.flatMap((entry) => {
    const location = Array.isArray(entry.loc)
      ? entry.loc.filter((part) => typeof part === "string" || typeof part === "number")
      : typeof entry.field === "string"
        ? [entry.field]
        : [];
    const message = [entry.msg, entry.message, entry.detail].find(
      (value): value is string => typeof value === "string" && value.length > 0,
    );
    if (!message) return [];
    const field = location.filter((part) => part !== "body").join(".");
    return [field ? `${field}: ${message}` : message];
  });
}

export class WrongAuthorityContextError extends Error {
  constructor() {
    super("The authority console requires a global EmpireAPI session.");
    this.name = "WrongAuthorityContextError";
  }
}

export function presentError(error: unknown): PresentedError {
  if (error instanceof EmpireAPIError) {
    const mapping: Record<number, Pick<PresentedError, "kind" | "title">> = {
      400: { kind: "validation", title: "Check the submitted details" },
      401: { kind: "authentication", title: "Your session has ended" },
      403: { kind: "forbidden", title: "Permission required" },
      404: { kind: "not-found", title: "Resource not found" },
      409: { kind: "conflict", title: "The resource changed" },
      422: { kind: "validation", title: "Check the submitted details" },
      429: { kind: "rate-limit", title: "Too many requests" },
    };
    const fallback =
      error.status >= 500
        ? { kind: "transient" as const, title: "EmpireAPI is unavailable" }
        : { kind: "unexpected" as const, title: "The request failed" };
    const presentation = mapping[error.status] ?? fallback;

    return {
      ...presentation,
      message: error.detail,
      status: error.status,
      code: error.code,
      requestId: error.requestId,
      validationIssues: validationIssues(error.errors),
    };
  }

  if (error instanceof WrongAuthorityContextError) {
    return {
      kind: "authentication",
      title: "Wrong EmpireAPI session",
      message: error.message,
      status: null,
      code: "wrong_authority_context",
      requestId: null,
      validationIssues: [],
    };
  }

  return {
    kind: "transient",
    title: "Could not reach EmpireAPI",
    message: error instanceof Error ? error.message : "An unexpected connection error occurred.",
    status: null,
    code: null,
    requestId: null,
    validationIssues: [],
  };
}

export function diagnosticsText(action: string, error: PresentedError): string {
  return [
    `Action: ${action}`,
    `Time: ${new Date().toISOString()}`,
    error.status === null ? null : `Status: ${error.status}`,
    error.code === null ? null : `Code: ${error.code}`,
    error.requestId === null ? null : `Request ID: ${error.requestId}`,
    ...error.validationIssues.map((issue) => `Validation: ${issue}`),
  ]
    .filter((line): line is string => line !== null)
    .join("\n");
}
